import React, { useState, useEffect, useRef, useCallback } from 'react';
import MeterDisplay from './MeterDisplay';
import FlagTripModal from './FlagTripModal';
import EditTripModal from './EditTripModal';
import SwipeStatusControl from './SwipeStatusControl';
import {
  PASSENGER_DETERMINED_DROP,
  isMissingDropoffAddressText,
  normalizeMapLatLng,
  isValidMapPosition,
  readGoogleLatLng,
} from './locationHelper';
import { startNativeNavigation } from '../../../utils/nativeNavigation';
import { reverseGeocodeCoords } from '../FlagTrip/addressValidation';
import { isPaidState } from '../../../utils/tripPaymentStatus';

// Trip-edit gating (#1 Edit Trip): the driver may amend a booking only when it is
// a flag trip (arrives with no destination — "Passenger Determined") OR a cash /
// credit-card trip (no account billing). Account-billed trips are not editable
// here. The persistent client signal for billing is `waypoint.account`; there is
// no dedicated paymentType field on the waypoint (called out in the task report).
const CASH_OR_CARD_ACCOUNTS = new Set([
  '',
  'cash',
  'card',
  'credit',
  'creditcard',
  'credit card',
  'none',
  'private',
]);

function isFlagWaypoint(waypoint) {
  return (
    waypoint?.type === 'Dropoff' &&
    isMissingDropoffAddressText(waypoint?.address1) &&
    Array.isArray(waypoint?.location) &&
    Number(waypoint.location[0]) === 0 &&
    Number(waypoint.location[1]) === 0
  );
}

// Prefer the server's authoritative accountBilled flag (paymentAPI.summarizeTrip);
// the account-name string match below is a heuristic that predates having it, and it
// misreads any account label not in the hardcoded set. Falls back to the heuristic
// only when no payment block has arrived for the trip yet.
function isCashOrCardTrip(waypoint, paymentStatus) {
  if (paymentStatus && paymentStatus.state !== 'unknown') {
    return paymentStatus.state !== 'accountBilled';
  }
  const acct = String(waypoint?.account || '').trim().toLowerCase();
  return CASH_OR_CARD_ACCOUNTS.has(acct);
}

const CurrentWaypoint = ({
  waypoint,
  expectedTime,
  /** { minutes, usesTraffic } — driving time from Directions when Map route runs */
  routeDriveSummary = null,
  isAccurate,
  isPhone,
  onWaypointAction,
  requestDirections,
  softMeterState,
  softMeterRouteOverride = null,
  onSetSoftMeterRouteOverride,
  onClearSoftMeterRouteOverride,
  meterRatesReady = false,
  /**
   * Flag Trip is only visible when the driver has no in-progress trip AND no meter is running.
   * Computed in LiveDispatch (currentTrip status not in {Dispatched, Enroute, ArrivedOrigin,
   * Loaded, ArrivedDestination} AND meterRunning === false), gated by fleet allowFlag setting.
   */
  canFlag = false,
  fareConfirmationOpen = false,
  center,
  onFlagTripModalClosed,
  onFlagTripModalOpen,
  onFlagTripModalDismiss,
  registerPendingFlagAck,
  onOpenAddOns,
  onOpenPayment,
  /** Opens the trip marketplace. Only offered while the driver is idle. */
  onBrowseMarketplace,
  /** True once trip:paymentSettled has arrived for this waypoint's trip (PAYMENT_DRIVERAPP_HANDOFF.md §3). */
  tripPaid = false,
  /** Resolved server payment status for this waypoint's trip (utils/tripPaymentStatus). */
  tripPaymentStatus = null,
  /** Live trip status from LiveDispatch (currentTrip.status). Authoritative over
   *  the waypoint's own copy, which is only stamped at JOB ingestion. */
  tripStatus = null,
}) => {

const [isArrived, setIsArrived] = useState(waypoint?.arrived || false);
// Pickup address reverse-geocoded from tripPickupLat/Lon, used only when dispatch sent
// coords without any street text for the origin leg.
const [geocodedPickup, setGeocodedPickup] = useState('');
// Short-lived confirmation shown after a swipe/tap action (see showActionNotice).
const [actionNotice, setActionNotice] = useState('');
const actionNoticeTimerRef = useRef(null);
const [isCallout, setIsCallout] = useState(false);
const [showActionsDropdown, setShowActionsDropdown] = useState(false);
const [hasUsedMap, setHasUsedMap] = useState(false);
const [showFlagModal, setShowFlagModal] = useState(false);
const [showRouteEditor, setShowRouteEditor] = useState(false);
const [routeFormError, setRouteFormError] = useState('');
const [routeForm, setRouteForm] = useState({ destinationText: '' });
  const [placePredictions, setPlacePredictions] = useState([]);
  const [showEditTrip, setShowEditTrip] = useState(false);
  const placeIdFromSuggestionRef = useRef(null);
  const pickedDescriptionRef = useRef('');
  const debouncePredictionsRef = useRef(null);
  const showFlagButton = !!canFlag;
  const meterRunning = !!softMeterState?.isRunning;

  // #1 Edit Trip — only flag trips or cash/credit-card trips are editable.
  const flagAwaitingDestination = isFlagWaypoint(waypoint);
  const canEditTrip = flagAwaitingDestination || isCashOrCardTrip(waypoint, tripPaymentStatus);

  useEffect(() => {
    setIsArrived(!!waypoint?.arrived);
    setIsCallout(false);
    setShowActionsDropdown(false);
    setShowEditTrip(false);
    setGeocodedPickup('');
    setActionNotice('');
  }, [waypoint?.jobNumber, waypoint?.type]);

  // Clear the notice timer on unmount so it can't fire against a gone component.
  useEffect(() => () => {
    if (actionNoticeTimerRef.current) clearTimeout(actionNoticeTimerRef.current);
  }, []);

  // Resolve a pickup street address from the origin coords when dispatch didn't send
  // any text for that leg. Only runs when there's genuinely nothing to display, so
  // normal trips (which arrive with a real address) never hit the geocoder.
  useEffect(() => {
    const lat = waypoint?.tripPickupLat;
    const lng = waypoint?.tripPickupLon;
    const hasText = !!(waypoint?.tripPickupAddress1 || waypoint?.tripPickupAddress2);
    if (hasText || !Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;

    let cancelled = false;
    reverseGeocodeCoords(lat, lng).then((resolved) => {
      if (cancelled || !resolved) return;
      const line = [
        [resolved.streetNumber, resolved.streetName].filter(Boolean).join(' '),
        resolved.city,
      ]
        .filter(Boolean)
        .join(', ');
      setGeocodedPickup(line || resolved.address || '');
    });
    return () => { cancelled = true; };
  }, [
    waypoint?.tripPickupLat,
    waypoint?.tripPickupLon,
    waypoint?.tripPickupAddress1,
    waypoint?.tripPickupAddress2,
  ]);

  useEffect(() => {
    if (!meterRunning) {
      setShowRouteEditor(false);
      setRouteFormError('');
      setPlacePredictions([]);
      placeIdFromSuggestionRef.current = null;
      pickedDescriptionRef.current = '';
    }
  }, [meterRunning]);

  const fetchPlacePredictions = useCallback(
    (input) => {
      const trimmed = String(input || '').trim();
      if (trimmed.length < 2 || !window.google?.maps?.places?.AutocompleteService) {
        setPlacePredictions([]);
        return;
      }
      const svc = new window.google.maps.places.AutocompleteService();
      const request = { input: trimmed };
      const biased = normalizeMapLatLng(center);
      if (biased && isValidMapPosition(biased)) {
        request.location = new window.google.maps.LatLng(biased.lat, biased.lng);
        request.radius = 50000;
      }
      svc.getPlacePredictions(request, (predictions, status) => {
        const OK = window.google.maps.places.PlacesServiceStatus.OK;
        const ZERO = window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS;
        if (status !== OK && status !== ZERO) {
          setPlacePredictions([]);
          return;
        }
        setPlacePredictions(predictions || []);
      });
    },
    [center]
  );

  useEffect(() => {
    if (!showRouteEditor || !meterRunning) {
      if (debouncePredictionsRef.current) {
        clearTimeout(debouncePredictionsRef.current);
        debouncePredictionsRef.current = null;
      }
      return;
    }
    const trimmed = String(routeForm.destinationText || '').trim();
    if (trimmed.length < 2) {
      setPlacePredictions([]);
      return;
    }
    if (
      placeIdFromSuggestionRef.current &&
      trimmed === pickedDescriptionRef.current
    ) {
      return;
    }
    if (debouncePredictionsRef.current) clearTimeout(debouncePredictionsRef.current);
    debouncePredictionsRef.current = setTimeout(() => {
      debouncePredictionsRef.current = null;
      fetchPlacePredictions(trimmed);
    }, 280);
    return () => {
      if (debouncePredictionsRef.current) {
        clearTimeout(debouncePredictionsRef.current);
        debouncePredictionsRef.current = null;
      }
    };
  }, [
    showRouteEditor,
    meterRunning,
    routeForm.destinationText,
    fetchPlacePredictions,
  ]);

  const getDefaultRouteDraft = () => ({
    destinationText:
      softMeterRouteOverride?.destinationLabel ||
      String(waypoint?.address1 || '').trim(),
  });

  const openRouteEditor = () => {
    if (!meterRunning) return;
    placeIdFromSuggestionRef.current = null;
    pickedDescriptionRef.current = '';
    setPlacePredictions([]);
    const overrideOrigin = normalizeMapLatLng(softMeterRouteOverride?.origin);
    const overrideDestination = normalizeMapLatLng(softMeterRouteOverride?.destination);
    if (overrideOrigin && overrideDestination) {
      setRouteForm({
        destinationText:
          String(softMeterRouteOverride?.destinationLabel || '').trim() ||
          String(waypoint?.address1 || '').trim(),
      });
    } else {
      setRouteForm(getDefaultRouteDraft());
    }
    setRouteFormError('');
    setShowRouteEditor(true);
  };

  const handleRouteFormChange = (key, value) => {
    setRouteForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleDestinationTextChange = (value) => {
    placeIdFromSuggestionRef.current = null;
    pickedDescriptionRef.current = '';
    handleRouteFormChange('destinationText', value);
  };

  const handlePickPlacePrediction = (prediction) => {
    if (!prediction?.description || !prediction.place_id) return;
    placeIdFromSuggestionRef.current = prediction.place_id;
    pickedDescriptionRef.current = prediction.description;
    setRouteForm((prev) => ({ ...prev, destinationText: prediction.description }));
    setPlacePredictions([]);
    setRouteFormError('');
  };

  /** Geocoder.geocode is callback-only; geometry.location is google.maps.LatLng (needs readGoogleLatLng). */
  const geocodeAddressText = useCallback((address, biasCenter) => {
    return new Promise((resolve) => {
      if (!window.google?.maps?.Geocoder) {
        resolve({ results: [], status: 'NO_GEOCODER' });
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      const trimmed = String(address || '').trim();
      const biased = normalizeMapLatLng(biasCenter);
      const run = (request) => {
        geocoder.geocode(request, (results, status) => {
          resolve({
            results: status === window.google.maps.GeocoderStatus.OK ? results || [] : [],
            status,
          });
        });
      };
      if (biased && isValidMapPosition(biased)) {
        const pad = 0.6;
        run({
          address: trimmed,
          bounds: new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(biased.lat - pad, biased.lng - pad),
            new window.google.maps.LatLng(biased.lat + pad, biased.lng + pad)
          ),
        });
      } else {
        run({ address: trimmed });
      }
    });
  }, []);

  const handleRouteEditorSave = async () => {
    const origin = readGoogleLatLng(center) ?? normalizeMapLatLng(center);
    if (!origin || !isValidMapPosition(origin)) {
      setRouteFormError('Current source location is unavailable.');
      return;
    }
    const destinationText = String(routeForm.destinationText || '').trim();
    if (!destinationText) {
      setRouteFormError('Enter a destination name or address.');
      return;
    }
    const placeId = placeIdFromSuggestionRef.current;
    let destination = null;
    let destinationLabel = destinationText;

    if (placeId && window.google?.maps?.places?.PlacesService) {
      const details = await new Promise((resolve) => {
        const svc = new window.google.maps.places.PlacesService(document.createElement('div'));
        svc.getDetails(
          {
            placeId,
            fields: ['geometry', 'formatted_address', 'name'],
          },
          (place, status) => {
            const OK = window.google.maps.places.PlacesServiceStatus.OK;
            if (status !== OK || !place?.geometry?.location) {
              resolve(null);
              return;
            }
            resolve(place);
          }
        );
      });
      const loc = details?.geometry?.location;
      destination = readGoogleLatLng(loc);
      destinationLabel =
        String(details?.formatted_address || '').trim() || destinationText;
    }

    if (!destination || !isValidMapPosition(destination)) {
      if (!window.google?.maps?.Geocoder) {
        setRouteFormError('Map geocoder is not ready. Please try again.');
        return;
      }
      let { results } = await geocodeAddressText(destinationText, center);
      if (!results?.length) {
        const geocoder = new window.google.maps.Geocoder();
        results = await new Promise((resolve) => {
          geocoder.geocode({ address: destinationText }, (res, status) => {
            if (status === window.google.maps.GeocoderStatus.OK && res?.length) {
              resolve(res);
            } else {
              resolve([]);
            }
          });
        });
      }
      if (!results?.length) {
        const geocoder = new window.google.maps.Geocoder();
        results = await new Promise((resolve) => {
          geocoder.geocode(
            { address: destinationText, region: 'uk' },
            (res, status) => {
              if (status === window.google.maps.GeocoderStatus.OK && res?.length) {
                resolve(res);
              } else {
                resolve([]);
              }
            }
          );
        });
      }
      const first = results?.[0];
      const location = first?.geometry?.location;
      destination = readGoogleLatLng(location);
      destinationLabel =
        String(first?.formatted_address || '').trim() || destinationText;
    }

    if (!destination || !isValidMapPosition(destination)) {
      setRouteFormError('Could not find destination. Try a full address.');
      return;
    }
    placeIdFromSuggestionRef.current = null;
    pickedDescriptionRef.current = '';
    onSetSoftMeterRouteOverride?.({ origin, destination, destinationLabel });
    setShowRouteEditor(false);
    setRouteFormError('');
    setPlacePredictions([]);
  };

  /** Dispatch may send status casing variants */
  // `waypoint.status` is stamped when the JOB payload is ingested and is NOT
  // rewritten when the driver loads the passenger — it goes stale as soon as the
  // trip advances, which is why a Loaded trip could still read as not-loaded here.
  // `tripStatus` is LiveDispatch's live value (currentTrip.status) and wins when
  // supplied; the waypoint copy stays as the fallback for the first render.
  const isTripLoaded =
    String(tripStatus ?? waypoint?.status ?? '')
      .trim()
      .toLowerCase() === 'loaded';

  if (!waypoint) {
    return (
      <>
        <FlagTripModal
          isOpen={showFlagModal}
          onClose={() => {
            setShowFlagModal(false);
            onFlagTripModalDismiss?.();
          }}
          currentLocation={center}
          registerPendingFlagAck={registerPendingFlagAck}
          onSuccess={(jobNumber) => {
            setShowFlagModal(false);
            if (typeof onFlagTripModalClosed === 'function') onFlagTripModalClosed(jobNumber);
          }}
        />
        <div className="w-full flex flex-col items-center justify-center py-6 px-3 bg-white border-t border-gray-200 rounded-b">
          <h2 className="text-center text-lg text-gray-800 mb-3">No Assigned Trips</h2>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {showFlagButton && (
              <button
                type="button"
                className="text-white font-bold py-2 px-4 rounded bg-orange-500 hover:bg-orange-600 shadow"
                onClick={() => {
                  onFlagTripModalOpen?.();
                  setShowFlagModal(true);
                }}
              >
                Flag Trip
              </button>
            )}
            {onBrowseMarketplace && (
              <button
                type="button"
                className="rounded bg-brand py-2 px-4 font-bold text-white shadow transition-transform active:scale-95"
                onClick={onBrowseMarketplace}
              >
                Available Trips
              </button>
            )}
          </div>
        </div>
      </>
    );
  }

  // Transient inline confirmation. These actions fire a fire-and-forget socket message
  // (DEST/ORIG/CALLM) with no server ack, and the only previous acknowledgement of a
  // callout was a "✓ Called Out" label INSIDE the closed Actions dropdown — so from the
  // driver's side a successful swipe looked identical to a dead control.
  const showActionNotice = (text) => {
    setActionNotice(text);
    if (actionNoticeTimerRef.current) clearTimeout(actionNoticeTimerRef.current);
    actionNoticeTimerRef.current = setTimeout(() => setActionNotice(''), 4000);
  };

  const handleArrive = () => {
    if (waypoint && waypoint.jobNumber) {
      setIsArrived(true);
      if (onWaypointAction) {
        onWaypointAction('arrive', waypoint.jobNumber, waypoint.type);
      }
      showActionNotice(
        waypoint.type === 'Dropoff' ? 'Arrival at dropoff sent to dispatch.' : 'Arrival sent to dispatch.'
      );
    } else {
      console.error('Waypoint or job number is missing.');
    }
    setShowActionsDropdown(false);
  };

  const handleLoad = () => {
    if (waypoint && waypoint.jobNumber) {
      if (onWaypointAction) {
        onWaypointAction('load', waypoint.jobNumber, waypoint.type);
      }
      setIsArrived(false);
    } else {
      console.error('Waypoint or job number is missing.');
    }
  };

  const handleComplete = () => {
    if (waypoint && waypoint.jobNumber) {
      if (onWaypointAction) {
        onWaypointAction('complete', waypoint.jobNumber, waypoint.type);
      }
    } else {
      console.error('Waypoint or job number is missing.');
    }
  };

  /**
   * Payment collection at the dropoff waypoint only surfaces when the
   * passenger actually owes money: a cash/card trip (not account-billed, see
   * isCashOrCardTrip) with an active fare. Account-billed / no-fare trips
   * complete directly, matching 02-trip-lifecycle.md.
   */
  const handleEndTrip = () => {
    // Already paid (set-price trip settled before arrival, or collected mid-trip)
    // — complete immediately, no curbside collection step (PAYMENT_DRIVERAPP_HANDOFF.md §5).
    //
    // "Owes money" must NOT depend on the live soft meter: most trips are fixed-fare
    // and never start a meter at all, so softMeterState is null for them (see
    // LiveDispatch.js's `meterRunning ? {...} : null`). Using softMeterState.fare here
    // meant every non-metered cash/card trip silently skipped payment collection. The
    // server-authoritative paid/unpaid state (the same one driving the Paid/Unpaid
    // chip above) is the correct signal for whether anything is still owed.
    const isTripPaidNow = tripPaymentStatus ? isPaidState(tripPaymentStatus.state) : tripPaid === true;
    const owesMoney = isCashOrCardTrip(waypoint, tripPaymentStatus) && !isTripPaidNow;
    if (owesMoney && onOpenPayment) {
      onOpenPayment(waypoint, 'endTrip');
    } else {
      handleComplete();
    }
  };

  const handleCallout = () => {
    if (waypoint && waypoint.jobNumber) {
      setIsCallout(true);
      if (onWaypointAction) {
        onWaypointAction('callout', waypoint.jobNumber, waypoint.type);
      }
      showActionNotice('Dispatch notified — they will contact you.');
    } else {
      console.error('Waypoint or job number is missing.');
    }
    setShowActionsDropdown(false);
  }

  const handleNoShow = () => {
    if (waypoint && waypoint.jobNumber) {
      if (onWaypointAction) {
        onWaypointAction('noshow', waypoint.jobNumber, waypoint.type);
      }
    } else {
      console.error('Waypoint or job number is missing.');
    }
    setShowActionsDropdown(false);
  };

  const handleMapDirections = () => {
    if (requestDirections) {
      requestDirections();
      setHasUsedMap(true); // Track that map has been used
    }
    setShowActionsDropdown(false);
  };

  // Turn-by-turn navigation to this waypoint. Distinct from handleMapDirections
  // above, which only draws the route on the in-app map — this hands the driver
  // off to real navigation. Mirrors ItineraryCurrentWaypoint's proven approach:
  // inside the Android WebView the native bridge launches Google Maps directly;
  // everywhere else (iOS Safari, desktop, local dev) startNativeNavigation
  // no-ops and returns false, so we fall back to the universal web deep link
  // that works on both iOS and Android.
  const navCoords = normalizeMapLatLng(waypoint?.location);
  const canNavigate = isValidMapPosition(waypoint?.location);

  const handleNavigate = () => {
    setShowActionsDropdown(false);
    if (!navCoords) {
      console.error('Cannot navigate - waypoint missing GPS coordinates');
      return;
    }
    const label = waypoint.paxName || waypoint.address1 || waypoint.type || 'Destination';
    if (startNativeNavigation(navCoords.lat, navCoords.lng, '', true, label)) return;
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${navCoords.lat},${navCoords.lng}`,
      '_blank'
    );
  };

  // Full Pickup -> Dropoff pair for the card header. LiveDispatch attaches both legs'
  // addresses to every waypoint (tripPickupAddress*/tripDropoffAddress*); fall back to
  // this waypoint's own address for the matching leg when they're absent (older
  // payloads, or a synthesized waypoint), so the labels are never blank unnecessarily.
  const joinAddressLines = (line1, line2) =>
    [line1, line2 && line2 !== line1 ? line2 : ''].filter(Boolean).join(', ');

  const pickupAddressFromPayload =
    joinAddressLines(waypoint.tripPickupAddress1, waypoint.tripPickupAddress2) ||
    (waypoint.type === 'Pickup' ? joinAddressLines(waypoint.address1, waypoint.address2) : '');

  // Dispatch often sends a flag trip's origin as coordinates with no street text, which
  // left this reading "Address not provided" even though we know exactly where it is.
  // geocodedPickup (resolved in the effect below) fills that gap.
  const pickupLineText =
    pickupAddressFromPayload ||
    geocodedPickup ||
    (Number.isFinite(waypoint.tripPickupLat) ? 'Looking up address…' : 'Address not provided');

  const rawDropoffLine =
    joinAddressLines(waypoint.tripDropoffAddress1, waypoint.tripDropoffAddress2) ||
    (waypoint.type === 'Dropoff' ? joinAddressLines(waypoint.address1, waypoint.address2) : '');
  // A flag trip's dropoff is chosen by the passenger en route, so an empty/placeholder
  // value here is expected rather than an error — show the same wording the rest of the
  // app uses for it instead of a bare blank.
  const dropoffLineText = isMissingDropoffAddressText(rawDropoffLine)
    ? PASSENGER_DETERMINED_DROP
    : rawDropoffLine;

  // ACET doesn't run SoftMeter, so its drivers have no action to take on a "rate card
  // hasn't arrived" notice — it's just alarming noise on every trip. Same fleet-code
  // resolution the Dashboard/Sidebar gates already use (localStorage fallback included).
  const isAcetFleet =
    (localStorage.getItem('fleetCode') || '').toUpperCase() === 'ACET';

  /**
   * Server-authoritative payment status chip, rendered on BOTH the Pickup and Dropoff
   * cards. Defined once here rather than inlined twice so the two legs can't drift.
   *
   * Replaces a bare `tripPaid` boolean that could only ever say "Paid": it showed
   * nothing for account-billed, pending, not-yet-final or unpaid trips, and — because
   * tripPaid came from a session-only Set — nothing at all for a trip that was already
   * paid before this mount. `tripPaid` is kept as the fallback for the window before
   * any payment block has arrived.
   */
  // Two values only, and ALWAYS rendered — matching the itinerary waypoint card. The
  // previous version hid itself whenever the server sent no payment block (state
  // 'unknown' -> empty label -> null), which is why the card showed no status at all.
  // A trip with no payment data reads "Unpaid": if that's wrong the driver checks and
  // finds it settled, whereas a wrong "Paid" silently skips collecting a real fare.
  const isTripPaid = tripPaymentStatus
    ? isPaidState(tripPaymentStatus.state)
    : tripPaid === true;
  // Mid-trip collection (DRIVERAPP_HOSTED_PAGE_PAYMENTS.md: the RiderApp QR may be
  // shown while the trip is Loaded, not only after arrival).
  //
  // Deliberately NOT gated on a final fare. The QR encodes only the trip id — it
  // moves no money and asks the server for nothing. When the fare is not final yet
  // the RiderApp tells the passenger "fare will be finalized at drop-off" and
  // withholds its Pay button, so showing the code early is harmless and lets the
  // passenger onboard while the trip is still running. The sheet's own guards
  // still withhold the hosted card page until the fare IS final, and completion
  // remains on the normal arrival step.
  //
  // Hidden only when there is genuinely nothing to collect: settled, billed to an
  // account, or a final $0 fare.
  const paymentQrState = tripPaymentStatus?.state;
  const nothingToCollect =
    paymentQrState === 'paid' ||
    paymentQrState === 'accountBilled' ||
    paymentQrState === 'noCharge';
  const canOfferPaymentQr = !!onOpenPayment && isTripLoaded &&
    isCashOrCardTrip(waypoint, tripPaymentStatus) && !nothingToCollect;
  // Bare pill — the header's right column handles alignment, so no wrapper div here.
  // Rendered ONCE, in the trip-info header next to Schedule Time.
  const paymentStatusChip = (
    <span
      className={`mt-1 inline-flex flex-shrink-0 items-center gap-1 self-end whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
        isTripPaid
          ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
          : 'bg-orange-50 text-orange-700 border-orange-300'
      }`}
    >
      {isTripPaid && <span aria-hidden>✓</span>}
      {isTripPaid ? 'Paid' : 'Unpaid'}
    </span>
  );

  /** SoftMeter lives under Dropoff historically; flag / loaded pickup needs the same UI on Pickup. */
  const needsMeterSpace =
    waypoint.type === 'Dropoff' ||
    !!softMeterState?.isRunning ||
    (isTripLoaded && meterRatesReady);

  return (
    <>
    <FlagTripModal
      isOpen={showFlagModal}
      onClose={() => {
        setShowFlagModal(false);
        onFlagTripModalDismiss?.();
      }}
      currentLocation={center}
      registerPendingFlagAck={registerPendingFlagAck}
      onSuccess={(jobNumber) => {
        setShowFlagModal(false);
        if (typeof onFlagTripModalClosed === 'function') onFlagTripModalClosed(jobNumber);
      }}
    />
    <EditTripModal
      isOpen={showEditTrip}
      waypoint={waypoint}
      center={center}
      onClose={() => setShowEditTrip(false)}
      onSaved={() => setShowEditTrip(false)}
    />
    <div className='w-full flex flex-col bg-white'
      style={{
        cursor: 'pointer',
        borderRadius: '0px 0px 5px 5px',
        borderTop: '1px solid lightgray',
        minHeight: !isPhone ? '280px' : undefined,
      }}>

      <div className='flex w-full items-stretch flex-1'>
        {waypoint.type === 'Pickup' && (
          <div
            className='w-1 self-stretch flex-shrink-0'
            style={{ backgroundColor: waypoint.wheelChair ? 'rgb(66 186 240)' : 'rgb(255 210 5)' }}
          />
        )}

        {/* main card content */}
        <div className='flex flex-col w-full min-w-0 flex-1'>
          <div className='flex flex-col px-3 py-2 flex-1 min-h-0 min-w-0'>
            <div className='flex items-start gap-2'>
              <div className='flex flex-col flex-1 min-w-0' >
                <div
                  className='font-light italic mb-1 text-xs truncate'
                  style={{ color: 'rgb(0 100 255)' }}
                >
                  {waypoint.wheelChair && 'Wheelchair'}
                </div>
                {/* Both legs, explicitly labelled — the card used to show only the
                    current waypoint's own address, so a driver on the dropoff leg
                    couldn't see where the trip started (and vice versa). */}
                <p className='text-sm text-left overflow-hidden whitespace-nowrap text-ellipsis'>
                  <span className='font-bold'>Pick up: </span>
                  <span className='font-semibold'>{pickupLineText}</span>
                </p>
                <p className='text-sm text-left overflow-hidden whitespace-nowrap text-ellipsis'>
                  <span className='font-bold'>Drop off: </span>
                  <span className='font-semibold'>{dropoffLineText}</span>
                </p>
              </div>
              {/* Auto-width (not a fixed 1/3) so "Scheduled: 4:33 AM" always
                  fits on one line — a fixed fraction squeezed it into wrapping
                  on narrower phones. The address column flexes instead. */}
              <div className='text-xs flex flex-col flex-shrink-0 items-end' >
                {/* Always rendered (with N/A) rather than hidden behind a truthy check —
                    an on-demand flag trip often has no eta at all, which previously left
                    this slot completely blank with no explanation. Prefers the trip's
                    scheduled PICKUP time; falls back to this leg's own eta. */}
                <p className='whitespace-nowrap text-right'>
                  <span className='font-bold'>Schedule Time :- </span>
                  {waypoint.tripPickupTime || waypoint.time || 'N/A'}
                </p>
                {/* Payment status sits with the trip info, directly under Schedule Time —
                    the enclosing column is items-end/flex-shrink-0, so it stays
                    right-aligned and can't wrap into the address column on narrow
                    screens. Previously rendered below the action buttons, crowding the
                    swipe control. */}
                {paymentStatusChip}
                {hasUsedMap && (
                  <>
                    <p className='whitespace-nowrap text-right'>
                      <span className='font-bold'>Expected: </span>
                      {isAccurate ? expectedTime || waypoint.time : 'Calculating...'}
                    </p>
                    {isAccurate && routeDriveSummary && (
                      <p className='ml-2 text-right text-gray-600' style={{ fontSize: '11px', lineHeight: 1.3 }}>
                        ~{routeDriveSummary.minutes} min
                        {routeDriveSummary.routePreference === 'shortest' ? ' · shortest' : ' · fastest'}
                        {routeDriveSummary.distanceMeters != null && routeDriveSummary.distanceMeters > 0
                          ? ` · ${(routeDriveSummary.distanceMeters / 1609.34).toFixed(1)} mi`
                          : ''}
                        {routeDriveSummary.usesTraffic ? ' · traffic' : ''}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className='flex justify-between items-center mt-1 mb-1 min-w-0'>
              {/* Passenger / trip name. The leg type is no longer prefixed here — the
                  Pick up / Drop off labels above already say which leg is which, so
                  this read "Dropoff: Flag Trip" redundantly. */}
              <div className='overflow-hidden whitespace-nowrap text-ellipsis min-w-0 flex-1'>
                <span className='font-semibold'>{waypoint.paxName}</span>
                {waypoint.phone && <span className='text-sm ml-1'> -- {waypoint.phone}</span>}
              </div>
              {onOpenAddOns && (
                <button
                  type="button"
                  className="text-xs text-blue-700 underline flex-shrink-0 ml-2"
                  onClick={() => onOpenAddOns(waypoint)}
                >
                  + Add Charge
                </button>
              )}
              {meterRunning && waypoint.type === 'Dropoff' && (
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <button
                    type="button"
                    className="text-xs text-blue-700 underline"
                    onClick={openRouteEditor}
                  >
                    Edit Source / Destination
                  </button>
                  {softMeterRouteOverride && (
                    <button
                      type="button"
                      className="text-xs text-gray-600 underline"
                      onClick={() => {
                        onClearSoftMeterRouteOverride?.();
                        setRouteFormError('');
                        setShowRouteEditor(false);
                      }}
                    >
                      Clear override
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Post-JOBMF prompt: a flag trip arrives with no destination — nudge the
                driver to add the destination & fare. Opens the same Edit Trip modal. */}
            {flagAwaitingDestination && (
              <button
                type="button"
                onClick={() => setShowEditTrip(true)}
                className="mt-1 mb-1 flex w-full items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-left"
              >
                <span className="text-xs font-medium text-blue-800">
                  Destination not set — add destination &amp; fare
                </span>
                <span className="text-xs font-semibold text-blue-700 underline">Add</span>
              </button>
            )}
            {meterRunning && waypoint.type === 'Dropoff' && showRouteEditor && (
              <div className="mb-2 border border-gray-200 rounded p-2 bg-gray-50">
                <div className="flex flex-col gap-1 text-xs">
                  <span className="text-gray-600">
                    Source uses current vehicle location automatically.
                  </span>
                  <label className="flex flex-col gap-1">
                    <span>Dropoff name / address</span>
                    <div className="relative z-20">
                      <input
                        type="text"
                        autoComplete="off"
                        value={routeForm.destinationText}
                        onChange={(e) => handleDestinationTextChange(e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1"
                        placeholder="Start typing — suggestions appear as you type"
                      />
                      {placePredictions.length > 0 && (
                        <div className="absolute left-0 right-0 top-full z-30 mt-0.5 rounded border border-gray-200 bg-white shadow-md">
                          <ul
                            className="max-h-48 overflow-y-auto"
                            role="listbox"
                          >
                            {placePredictions.map((p) => (
                              <li key={p.place_id} className="border-b border-gray-100 last:border-b-0">
                                <button
                                  type="button"
                                  className="w-full px-2 py-2 text-left text-xs hover:bg-blue-50"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => handlePickPlacePrediction(p)}
                                >
                                  <span className="font-medium text-gray-900">
                                    {p.structured_formatting?.main_text || p.description}
                                  </span>
                                  {p.structured_formatting?.secondary_text && (
                                    <span className="block text-gray-600">
                                      {p.structured_formatting.secondary_text}
                                    </span>
                                  )}
                                </button>
                              </li>
                            ))}
                          </ul>
                          <div className="flex justify-end border-t border-gray-100 px-2 py-1">
                            <span className="text-[10px] text-gray-500">Powered by Google</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                  {routeFormError && (
                    <p className="text-red-600">{routeFormError}</p>
                  )}
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300"
                      onClick={() => {
                        setShowRouteEditor(false);
                        setRouteFormError('');
                        setPlacePredictions([]);
                        placeIdFromSuggestionRef.current = null;
                        pickedDescriptionRef.current = '';
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                      onClick={handleRouteEditorSave}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
            {isTripLoaded && !meterRatesReady && !isAcetFleet && (
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-1">
                Meter rates for this trip were not received from dispatch. SoftMeter will stay hidden
                until the rate card arrives over the link (STARTM or JOBM).
              </p>
            )}
          </div>
          <div
            className='flex flex-col px-3 pb-2 pt-1 relative flex-shrink-0'
          >
            {/* Confirmation for the fire-and-forget status/callout messages — shared by
                the Pickup and Dropoff blocks below. */}
            {actionNotice && (
              <div className='mb-1 rounded bg-emerald-50 border border-emerald-200 px-2 py-1 text-xs text-emerald-800'>
                {actionNotice}
              </div>
            )}
            {waypoint.type === 'Pickup' && (
              <div className="flex flex-col w-full gap-1">
                {softMeterState?.isRunning && (
                  <MeterDisplay
                    fare={softMeterState.fare}
                    fareMode={softMeterState.fareMode}
                    distance={softMeterState.distance}
                    isRunning={softMeterState.isRunning}
                    elapsedTime={softMeterState.elapsedTime}
                    fareTimeSeconds={softMeterState.fareTimeSeconds}
                    waitTime={softMeterState.waitTime}
                    waitCharge={softMeterState.waitCharge}
                    distanceCharge={softMeterState.distanceCharge}
                    timeCharge={softMeterState.timeCharge}
                    flagRate={softMeterState.flagRate}
                  />
                )}
              <div className='flex w-full items-stretch gap-2'>
                {/* Actions Dropdown — secondary/fallback actions. The primary
                    on-trip transition is the swipe control below (spec
                    01-driver-app-shell.md §2); this stays available so nothing
                    that worked before is removed. Equal-width row (flex-1) so
                    Actions and Navigate match. */}
                <div className='relative flex-1'>
                  <button
                    className='w-full text-white font-bold py-2 px-3 rounded bg-orange-500 hover:bg-orange-600 text-sm'
                    onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                  >
                    Actions ▼
                  </button>
                  {showActionsDropdown && (
                    <div className='absolute bottom-full left-0 mb-1 bg-white border border-gray-300 rounded shadow-lg z-50 w-44'>
                      <button
                        className='block w-full text-left px-3 py-2.5 text-sm hover:bg-green-100 text-green-600'
                        onClick={handleMapDirections}
                      >
                        Map
                      </button>
                      <button
                        className='block w-full text-left px-3 py-2.5 text-sm  hover:bg-orange-100 text-orange-600'
                        onClick={handleCallout}
                        disabled={isCallout}
                      >
                        {isCallout ? '✓ Called Out' : 'Callout'}
                      </button>
                      <button
                        className='block w-full text-left px-3 py-2.5 text-sm hover:bg-blue-100 text-blue-600'
                        onClick={handleArrive}
                        disabled={isArrived}
                      >
                        {isArrived ? '✓ Arrived' : 'Arrive'}
                      </button>
                      <button
                        className='block w-full text-left px-3 py-2.5 text-sm hover:bg-red-100 text-red-600'
                        onClick={handleNoShow}
                      >
                        No Show
                      </button>
                      {showFlagButton && (
                        <button
                          type="button"
                          className="block w-full text-left px-3 py-2.5 text-sm hover:bg-yellow-100 text-yellow-700"
                          onClick={() => {
                            onFlagTripModalOpen?.();
                            setShowFlagModal(true);
                            setShowActionsDropdown(false);
                          }}
                        >
                          Flag trip
                        </button>
                      )}
                      {canEditTrip && (
                        <button
                          type="button"
                          className="block w-full text-left px-3 py-2.5 text-sm hover:bg-blue-100 text-blue-700"
                          onClick={() => {
                            setShowEditTrip(true);
                            setShowActionsDropdown(false);
                          }}
                        >
                          Edit Trip
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <button
                  className='text-white font-bold py-2 px-3 rounded flex-1 text-sm disabled:opacity-50'
                  style={{ backgroundColor: '#059669' }}
                  onClick={handleNavigate}
                  disabled={!canNavigate}
                  title={canNavigate ? 'Open turn-by-turn navigation' : 'No GPS coordinates for this stop'}
                >
                  Navigate
                </button>
              </div>

              {isArrived ? (
                <SwipeStatusControl
                  advanceLabel="Passenger Onboard"
                  exceptionLabel="Passenger Not Found"
                  onAdvance={handleLoad}
                  onException={handleNoShow}
                  hideTapButtons={isAcetFleet}
                  disabled={isTripLoaded}
                  exceptionColor="#B45309"
                />
              ) : (
                <SwipeStatusControl
                  advanceLabel="Confirm Arrived"
                  exceptionLabel="Contact Dispatch"
                  onAdvance={handleArrive}
                  onException={handleCallout}
                  hideTapButtons={isAcetFleet}
                />
              )}
              </div>
            )}
            {waypoint.type === 'Dropoff' &&
              <div className='flex flex-col w-full gap-1'>
                {/* SoftMeter — live fare while running */}
                {softMeterState?.isRunning && (
                  <MeterDisplay
                    fare={softMeterState.fare}
                    fareMode={softMeterState.fareMode}
                    distance={softMeterState.distance}
                    isRunning={softMeterState.isRunning}
                    elapsedTime={softMeterState.elapsedTime}
                    fareTimeSeconds={softMeterState.fareTimeSeconds}
                    waitTime={softMeterState.waitTime}
                    waitCharge={softMeterState.waitCharge}
                    distanceCharge={softMeterState.distanceCharge}
                    timeCharge={softMeterState.timeCharge}
                    flagRate={softMeterState.flagRate}
                  />
                )}
                <div className='flex w-full items-stretch gap-2'>
                  {/* Actions Dropdown — secondary/fallback actions; primary
                      transition is the swipe control below. Each control in this
                      row is flex-1 so Actions / Navigate / Complete render at
                      equal width instead of Complete swallowing the row. */}
                  <div className='relative flex-1'>
                    <button
                      className='w-full text-white font-bold py-2 px-3 rounded bg-orange-500 hover:bg-orange-600 text-sm'
                      onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                    >
                      Actions ▼
                    </button>
                    {showActionsDropdown && (
                      <div className='absolute bottom-full left-0 mb-1 bg-white border border-gray-300 rounded shadow-lg z-50 w-44'>
                        <button
                          className='block w-full text-left px-3 py-2.5 text-sm  hover:bg-green-100 text-green-600'
                          onClick={handleMapDirections}
                        >
                          Map
                        </button>
                        <button
                          className='block w-full text-left px-3 py-2.5 text-sm hover:bg-blue-100 text-blue-600'
                          onClick={handleArrive}
                          disabled={isArrived}
                        >
                          {isArrived ? '✓ Arrived' : 'Arrive'}
                        </button>
                        {canOfferPaymentQr && (
                          <button
                            type="button"
                            className="block w-full text-left px-3 py-2.5 text-sm hover:bg-blue-100 text-blue-700"
                            onClick={() => {
                              setShowActionsDropdown(false);
                              onOpenPayment(waypoint, 'midTrip');
                            }}
                          >
                            Payment QR
                          </button>
                        )}
                        {showFlagButton && (
                          <button
                            type="button"
                            className="block w-full text-left px-3 py-2.5 text-sm hover:bg-yellow-100 text-yellow-700"
                            onClick={() => {
                              onFlagTripModalOpen?.();
                              setShowFlagModal(true);
                              setShowActionsDropdown(false);
                            }}
                          >
                            Flag Trip
                          </button>
                        )}
                        {canEditTrip && (
                          <button
                            type="button"
                            className="block w-full text-left px-3 py-2.5 text-sm hover:bg-blue-100 text-blue-700"
                            onClick={() => {
                              setShowEditTrip(true);
                              setShowActionsDropdown(false);
                            }}
                          >
                            Edit Trip
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    className='text-white font-bold py-2 px-3 rounded flex-1 text-sm disabled:opacity-50'
                    style={{ backgroundColor: '#059669' }}
                    onClick={handleNavigate}
                    disabled={!canNavigate}
                    title={canNavigate ? 'Open turn-by-turn navigation' : 'No GPS coordinates for this stop'}
                  >
                    Navigate
                  </button>
                  <button
                    className='text-white font-bold py-2 px-3 rounded flex-1 text-sm'
                    style={{ backgroundColor: '#0072B2' }}
                    onClick={handleEndTrip}
                  >
                    Complete
                  </button>
                </div>

                {isArrived ? (
                  <SwipeStatusControl
                    advanceLabel="End Trip"
                    exceptionLabel="Contact Dispatch"
                    onAdvance={handleEndTrip}
                    onException={handleCallout}
                    hideTapButtons={isAcetFleet}
                  />
                ) : (
                  <SwipeStatusControl
                    advanceLabel="Confirm Arrived"
                    exceptionLabel="Contact Dispatch"
                    onAdvance={handleArrive}
                    onException={handleCallout}
                    hideTapButtons={isAcetFleet}
                  />
                )}
              </div>
            }
          </div>
        </div>

        {waypoint.type === 'Dropoff' && (
          <div
            className='w-1 self-stretch flex-shrink-0'
            style={{ backgroundColor: waypoint.wheelChair ? 'rgb(66 186 240)' : 'rgb(255 210 5)' }}
          />
        )}

      </div>
    </div>
    </>
  );
};

export default CurrentWaypoint;
